import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
} from 'react';

import { useIndexedDB } from './IndexedDBContext';
import api from '../services/api';

interface User {
  id: string;
  name: string;
  email: string;
  avatar_url: string;
  empresa: Empresa;
  empresa_id: string;
  id_origem: number;
  profissional: boolean;
  perfil: {
    descricao: string;
    acessos: [{ acesso: string; tem_acesso: boolean }];
  };
}

interface Empresa {
  nome_fantasia: string;
  url_frontend: string;
  utiliza_login: string;
  configsStr: string;
  whats_link: string;
  whats_formatado: string;
}

interface AuthState {
  token: string;
  user: User;
}

interface SignInCredentials {
  email: string;
  password: string;
}

interface AuthContextData {
  user: User;
  signIn(credentials: SignInCredentials): Promise<void>;
  signOut(): void;
  updateUser(user: User): void;
  verifyAccess(access: string): boolean;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: React.FC = ({ children }) => {
  const storage = useIndexedDB();
  const [data, setData] = useState<AuthState>({} as AuthState);

  const signIn = useCallback(
    async ({ email, password }) => {
      const urlEmpresa = await storage.lerIndexedDB('empresa');
      const response = await api.post('sessions', {
        email,
        password,
        url_frontend: urlEmpresa,
      });

      const { token, user } = response.data;
      storage.gravarIndexedDB('token', token);
      storage.gravarIndexedDB('user', user);

      //define header padrao para todas as requisicoes - executa quando loga
      api.defaults.headers.authorization = `Bearer ${token}`;

      setData({ token, user });
    },
    [storage],
  );

  useEffect(() => {
    async function loadAuthData() {
      const token: string | null = await storage.lerIndexedDB('token');
      const user: User | null = await storage.lerIndexedDB('user');

      if (token && user) {
        //define header padrao para todas as requisicoes - executa quando dá F5/refresh na página
        api.defaults.headers.authorization = `Bearer ${token}`;

        setData({ token, user }); // <-- adiciona isso!

        return {
          token,
          user,
        };
      }

      return {} as AuthState;
    }

    loadAuthData();
  }, [storage]);

  const signOut = useCallback(() => {
    storage.excluirIndexedDB('token');
    storage.excluirIndexedDB('user');

    setData({} as AuthState);
  }, [storage]);

  useEffect(() => {
    if (!data.token) return;

    const tokenData = JSON.parse(atob(data.token.split('.')[1]));
    const tokenExpiry = tokenData.exp * 1000;
    const currentTime = Date.now();
    const timeout = tokenExpiry - currentTime;

    if (timeout <= 0) {
      signOut();
    } else {
      const timer = setTimeout(() => {
        signOut();
      }, timeout);

      return () => clearTimeout(timer);
    }
  }, [data.token, signOut]);

  const updateUser = useCallback(
    (user: User) => {
      storage.gravarIndexedDB('user', user);

      setData({
        token: data.token,
        user,
      });
    },
    [setData, data.token, storage],
  );

  const verifyAccess = useCallback(
    (access: string): boolean => {
      if (!data.user) {
        return false;
      }

      if (!data.user.perfil) {
        return false;
      }

      const permissao = data.user.perfil.acessos.find(
        permissao => permissao.acesso === access,
      );

      if (!permissao) {
        return false;
      }

      if (!permissao.tem_acesso) {
        return false;
      }

      return true;
    },
    [data],
  );

  return (
    <AuthContext.Provider
      value={{ user: data.user, signIn, signOut, updateUser, verifyAccess }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }

  return context;
}

export { AuthProvider, useAuth };
